import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useActiveFund } from '../../hooks/useActiveFund';
import { getDealsByFunds, getRecentAuditLog, getRecentDealAuditLogs } from '../../services/dealService';
import { getFriendlyError } from '../../utils/formatters';
import { getSLAConfigsForFund, getDealSLAInfo } from '../../services/slaService';
import PortalLayout from '../../components/shared/Layout/PortalLayout';
import KPICard from '../../components/dashboard/KPICard';
import AttentionList from '../../components/dashboard/AttentionList';
import RecentActivity from '../../components/dashboard/RecentActivity';
import PipelineFunnel from '../../components/dashboard/PipelineFunnel';
import LoadingSpinner from '../../components/shared/UI/LoadingSpinner';

export default function DashboardPage() {
  const { user, userProfile, authReady } = useAuth();
  const { activeFundId, activeStreamId } = useActiveFund();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [slaMap, setSlaMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authReady || !user?.uid) return;
    if (!userProfile) return;

    const currentRoles = userProfile?.roles ||
      (userProfile?.role ? [userProfile.role] : []);
    const isAdmin = currentRoles.includes('admin');

    // Non-admin with no active fund — nothing to show
    if (!isAdmin && !activeFundId) {
      setLoading(false);
      return;
    }

    // Admin and principals can read system-wide /audit_log; analysts cannot
    const isAdminOrPrincipal = currentRoles.some(r =>
      ['admin', 'head_of_pipeline', 'cio', 'md'].includes(r)
    );

    // Admin with no fund selected → fetch across all funds ([] + isAdmin=true)
    // Admin with a fund selected → scope to that fund
    // Non-admin → scope to activeFundId only
    const activeFundIds = activeFundId ? [activeFundId] : [];

    setLoading(true);
    setLoadError(null);

    async function load() {
      try {
        // Fetch deals — getDealsByFunds([], true) returns ALL deals for admins
        let fetchedDeals;
        try {
          fetchedDeals = await getDealsByFunds(activeFundIds, isAdmin);
        } catch (err) {
          console.error('Dashboard: deals query failed', err.code, err.message);
          throw err;
        }
        setDeals(fetchedDeals);

        // Branch audit log query by role to avoid permission denied for analysts
        let log = [];
        try {
          if (isAdminOrPrincipal) {
            log = await getRecentAuditLog(activeFundIds, 10);
          } else {
            log = await getRecentDealAuditLogs(fetchedDeals, 10);
          }
        } catch (err) {
          console.error('Dashboard: audit log query failed', err.code, err.message);
          log = [];
        }
        setAuditLog(log);

        // Build SLA map — fetch configs for each unique fund in results
        const fundIds = activeFundId
          ? [activeFundId]
          : [...new Set(fetchedDeals.map(d => d.fund_id))];
        const slaConfigs = {};
        for (const fid of fundIds) {
          try {
            slaConfigs[fid] = await getSLAConfigsForFund(fid);
          } catch (err) {
            console.error('Dashboard: sla_config query failed for fund', fid, err.code, err.message);
            slaConfigs[fid] = {};
          }
        }
        const map = {};
        fetchedDeals.forEach(d => {
          map[d.id] = getDealSLAInfo(d, slaConfigs[d.fund_id] || []);
        });
        setSlaMap(map);
      } catch (err) {
        console.error('Dashboard load error:', err.message);
        setLoadError(getFriendlyError(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  // Re-run when auth readiness, user, or active fund/stream changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, user?.uid, userProfile, activeFundId, activeStreamId]);

  if (loading) return <PortalLayout><LoadingSpinner /></PortalLayout>;
  if (loadError) return <PortalLayout><div className="alert alert-error" style={{ margin: 24 }}>{loadError}</div></PortalLayout>;

  const userRoles = userProfile?.roles || (userProfile?.role ? [userProfile.role] : []);
  const activeDeals = deals.filter(d => d.status === 'active');
  const unassigned = activeDeals.filter(d => !d.assigned_analyst_id);
  const myAction = activeDeals.filter(d => {
    if (d.assigned_analyst_id === user?.uid && d.current_stage === 'analyst_review') return true;
    if (d.peer_reviewer_id === user?.uid && d.current_stage === 'peer_review') return true;
    if (userRoles.includes('head_of_pipeline') && d.current_stage === 'hop_review') return true;
    if (['head_of_pipeline','cio','md'].some(r => userRoles.includes(r)) && d.current_stage === 'iic_approval') return true;
    return false;
  });
  const slaBreaches = Object.values(slaMap).filter(s => s.status === 'red').length;

  return (
    <PortalLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', marginBottom: 4 }}>
          Welcome back, {userProfile?.full_name?.split(' ')[0]}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Here's what needs your attention today.</p>
      </div>

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <KPICard label="Active Deals" value={activeDeals.length} icon="📋"
          onClick={() => navigate('/pipeline?status=active')} />
        <KPICard label="Unassigned PIFs" value={unassigned.length} icon="⚠️"
          badge={unassigned.length} badgeColor="var(--color-error)"
          onClick={() => navigate('/pipeline?tab=unassigned')} />
        <KPICard label="Pending My Action" value={myAction.length} icon="⏳"
          badge={myAction.length} badgeColor="var(--color-accent)"
          onClick={() => navigate('/pipeline?tab=pending-action')} />
        <KPICard label="SLA Breaches" value={slaBreaches} icon="🔴"
          badge={slaBreaches} badgeColor="var(--color-error)"
          onClick={() => navigate('/pipeline?sla=breached')} />
      </div>

      {/* Middle Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <div className="card">
          <div className="card-header"><h2>Needs Your Attention</h2></div>
          <div className="card-body">
            <AttentionList deals={deals} slaMap={slaMap} currentUserId={user?.uid} userRoles={userRoles} />
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h2>Recent Activity</h2></div>
          <div className="card-body">
            <RecentActivity entries={auditLog} />
          </div>
        </div>
      </div>

      {/* Pipeline Funnel */}
      <div className="card">
        <div className="card-header"><h2>Pipeline Summary</h2></div>
        <div className="card-body">
          <PipelineFunnel deals={deals} />
        </div>
      </div>
    </PortalLayout>
  );
}
